import { Attr, HasMany, Model } from "spraypaint";

import {
  AI_AGENT,
  AI_AGENT_GROUPS,
  AI_FEATURE,
  AI_PROVIDER,
  AI_PROVIDER_MODEL,
  AI_SERVICE_FEATURE,
} from "shared/modules/AI/constants";
import type ContentVersion from "shared/resources/ContentVersion";
import { ApplicationRecord } from "shared/services/spraypaint";
import type { Nullable } from "shared/types/utility";

export interface AIFeatureConfigurationSafeSetting {
  category:
    | "HARM_CATEGORY_HATE_SPEECH"
    | "HARM_CATEGORY_DANGEROUS_CONTENT"
    | "HARM_CATEGORY_SEXUALLY_EXPLICIT"
    | "HARM_CATEGORY_HARASSMENT";
  threshold:
    | "OFF"
    | "BLOCK_LOW_AND_ABOVE"
    | "BLOCK_ONLY_HIGH"
    | "BLOCK_MEDIUM_AND_ABOVE";
}

export interface AIFeatureConfigurationSafeSettings {
  safety_settings: AIFeatureConfigurationSafeSetting[];
}

export interface AIFeatureConfigurationStopSequences {
  stop_sequences: string[];
}

@Model()
class AIFeatureConfiguration extends ApplicationRecord {
  static apiNamespace = "/ai";

  static jsonapiType = "feature_configurations";

  @Attr() agentPromptKey: keyof typeof AI_AGENT;

  @Attr() aiProvider: keyof typeof AI_PROVIDER;

  @Attr() aiProviderModel: keyof typeof AI_PROVIDER_MODEL;

  @Attr() aiRegionIds: Nullable<number[]>;

  @Attr() archivedAt: string;

  @Attr() beta: boolean;

  @Attr({ persist: false }) createdAt: string;

  @Attr() feature: keyof typeof AI_FEATURE;

  @Attr() featureLabel: string;

  @Attr() internalVersion: string;

  @Attr() notes: string;

  @Attr() outputFormat: "text" | "json";

  @Attr() outputFormatJson: Nullable<Record<string, unknown>>;

  @Attr() outputTokenLimit: number;

  @Attr() safetySettings: Nullable<AIFeatureConfigurationSafeSettings>;

  @Attr() serviceFeature: Nullable<keyof typeof AI_SERVICE_FEATURE>;

  @Attr() stopSequences: Nullable<AIFeatureConfigurationStopSequences>;

  @Attr() systemInstructions: string;

  @Attr() temperature: number;

  @Attr() topK: number;

  @Attr() topP: number;

  @Attr({ persist: false }) updatedAt: string;

  @HasMany() versions: ContentVersion[];

  agentName(): string {
    if (AI_AGENT[this.agentPromptKey]) {
      const agent = AI_AGENT[this.agentPromptKey];

      const group =
        AI_AGENT_GROUPS[agent.group as keyof typeof AI_AGENT_GROUPS];

      return `${group.name}: ${AI_AGENT[this.agentPromptKey].name}`;
    }

    return "";
  }

  featureName(): string {
    if (AI_FEATURE[this.feature]) {
      return AI_FEATURE[this.feature].name;
    }

    return "";
  }

  providerModelName(): string {
    if (AI_PROVIDER_MODEL[this.aiProviderModel]) {
      return AI_PROVIDER_MODEL[this.aiProviderModel].name;
    }

    return "";
  }

  providerName(): string {
    if (AI_PROVIDER[this.aiProvider]) {
      return AI_PROVIDER[this.aiProvider].name;
    }

    return "";
  }

  serviceFeatureName(): string {
    if (!this.serviceFeature) return "";

    if (AI_SERVICE_FEATURE[this.serviceFeature]) {
      return AI_SERVICE_FEATURE[this.serviceFeature].name;
    }

    return "";
  }
}

export default AIFeatureConfiguration;
